import { useState } from "react";
import { useMonofasicos, useCstPisCofinsCombo } from "@/features/fiscal/hooks/useFiscal";
import { apiClient } from "@/lib/apiClient";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { type TamanhoPagina } from "@/hooks/usePaginacaoLocal";
import type { MonofasicoDto, SalvarMonofasicoRequest } from "@/features/fiscal/types";
import { NcmSearchCombobox } from "@/components/NcmSearchCombobox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import { Search, Percent, Plus, Pencil, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { AvisoTabelaNacional } from "@/components/AvisoTabelaNacional";

const TRIBUTACOES = ["Monofásico", "Subst. Tributária", "Alíquota Zero"];

const EMPTY: SalvarMonofasicoRequest = {
  codigoNcm:            "",
  segmentoAtividade:    1,
  regimeTributacao:     1,
  tributacao:           "Monofásico",
  cstPis:               "",
  cstCofins:            "",
  aliqPis:              0,
  aliqCofins:           0,
  aliquotaCondicionada: false,
  condicoes:            "",
  vigencia:             "VIGENTE",
  novoNcm:              "",
  dtInicioVigencia:     null,
  dtFimVigencia:        null,
  grupoBeneficiado:     "",
  origemInformacao:     "",
  baseLegal:            "",
  observacoes:          "",
};

function CstSelect({ value, options, onChange, placeholder = "Selecione..." }: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const opts = value && !options.some(o => o.value === value) ? [{ value, label: value }, ...options] : options;
  return (
    <Select value={value || "__none__"} onValueChange={v => onChange(v === "__none__" ? "" : v)}>
      <SelectTrigger className="font-mono"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">— Nenhum —</SelectItem>
        {opts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function Monofasico() {
  const { user } = useAuth();
  const podeEditar = user?.isAdminGlobal ?? false;
  const [busca, setBusca]           = useState("");
  const [pagina, setPagina]         = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId]     = useState<number | null>(null);
  const [editId, setEditId]         = useState<number | null>(null);
  const [form, setForm]             = useState<SalvarMonofasicoRequest>(EMPTY);
  const [saving, setSaving]         = useState(false);
  const [sincronizando, setSincronizando] = useState(false);

  const { query, obterPorId, criar, atualizar, excluir } = useMonofasicos(busca || undefined, pagina, tamanhoPagina);
  const itens = query.data?.itens ?? [];

  const { data: pisList = [] } = useCstPisCofinsCombo();
  const pisOpts = Array.from(new Map(pisList.filter(c => c.cst).map(c => [c.cst!, c])).values())
    .map(c => ({ value: c.cst!, label: `${c.cst} — ${c.descricao ?? ""}` }));

  const totalItens = query.data?.total ?? 0;
  const primeiroItem = totalItens === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const ultimoItem = Math.min(pagina * tamanhoPagina, totalItens);

  const set = (field: keyof SalvarMonofasicoRequest, value: unknown) =>
    setForm(f => ({ ...f, [field]: value }));

  const openNew = () => { setEditId(null); setForm(EMPTY); setDialogOpen(true); };

  const openEdit = async (id: number) => {
    try {
      const d: MonofasicoDto = await obterPorId(id);
      setEditId(id);
      setForm({
        codigoNcm:            d.codigoNcm ?? "",
        segmentoAtividade:    d.segmentoAtividade,
        regimeTributacao:     d.regimeTributacao,
        tributacao:           d.tributacao ?? "Monofásico",
        cstPis:               d.cstPis ?? "",
        cstCofins:            d.cstCofins ?? "",
        aliqPis:              d.aliqPis,
        aliqCofins:           d.aliqCofins,
        aliquotaCondicionada: d.aliquotaCondicionada,
        condicoes:            d.condicoes ?? "",
        vigencia:             d.vigencia ?? "",
        novoNcm:              d.novoNcm ?? "",
        dtInicioVigencia:     d.dtInicioVigencia?.substring(0, 10) ?? null,
        dtFimVigencia:        d.dtFimVigencia?.substring(0, 10) ?? null,
        grupoBeneficiado:     d.grupoBeneficiado ?? "",
        origemInformacao:     d.origemInformacao ?? "",
        baseLegal:            d.baseLegal ?? "",
        observacoes:          d.observacoes ?? "",
      });
      setDialogOpen(true);
    } catch {
      toast.error("Erro ao carregar monofásico.");
    }
  };

  const handleSave = async () => {
    if (!form.codigoNcm?.trim()) { toast.error("NCM é obrigatório."); return; }
    setSaving(true);
    try {
      if (editId) {
        await atualizar.mutateAsync({ id: editId, req: form });
        toast.success("Monofásico atualizado.");
      } else {
        await criar.mutateAsync(form);
        toast.success("Monofásico criado.");
      }
      setDialogOpen(false);
    } catch {
      toast.error("Erro ao salvar monofásico.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    try {
      await excluir.mutateAsync(deleteId);
      toast.success("Monofásico excluído.");
    } catch {
      toast.error("Erro ao excluir monofásico.");
    } finally {
      setDeleteId(null);
    }
  };

  const handleSync = async () => {
    setSincronizando(true);
    try {
      const { data } = await apiClient.post("/api/cadastros/fiscal/sacfiscal/sincronizar/monofasico");
      toast.success(`Sincronizado da SACFiscal: ${data.inseridos} novos, ${data.atualizados} atualizados.`);
      await query.refetch();
    } catch {
      toast.error("Falha ao sincronizar com a SACFiscal (verifique a chave em configuração).");
    } finally {
      setSincronizando(false);
    }
  };

  const tribVariant = (t: string | null) =>
    t === "Monofásico" ? "default" : t === "Alíquota Zero" ? "secondary" : "outline";

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Percent className="h-6 w-6" /> Monofásicos PIS/COFINS
          </h1>
          <p className="text-muted-foreground">Tabela EFD Contribuições (4.3.10) por NCM, segmento e regime</p>
        </div>
        <div className="flex gap-2">
          {podeEditar && (
            <Button variant="outline" onClick={handleSync} disabled={sincronizando}>
              <RefreshCw className={`h-4 w-4 mr-2 ${sincronizando ? "animate-spin" : ""}`} />
              {sincronizando ? "Sincronizando..." : "Sincronizar SACFiscal"}
            </Button>
          )}
          {podeEditar && (
            <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" /> Novo</Button>
          )}
        </div>
      </div>

      {!podeEditar && <AvisoTabelaNacional />}

      {/* Busca */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por NCM, grupo ou tributação..."
          className="pl-9"
          value={busca}
          onChange={e => { setBusca(e.target.value); setPagina(1); }}
        />
      </div>

      {/* Tabela */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">NCM</TableHead>
              <TableHead className="text-center w-[70px]">Segm.</TableHead>
              <TableHead className="text-center w-[70px]">Reg.</TableHead>
              <TableHead className="w-[140px]">Tributação</TableHead>
              <TableHead className="text-center w-[70px]">CST</TableHead>
              <TableHead className="text-center w-[80px]">% PIS</TableHead>
              <TableHead className="text-center w-[90px]">% COFINS</TableHead>
              <TableHead>Grupo</TableHead>
              <TableHead className="w-[80px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : itens.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Nenhum registro. Use "Sincronizar SACFiscal" para popular.</TableCell></TableRow>
            ) : itens.map(m => (
              <TableRow key={m.id}>
                <TableCell className="font-mono font-semibold">{m.codigoNcm ?? "—"}</TableCell>
                <TableCell className="text-center text-sm">{m.segmentoAtividade}</TableCell>
                <TableCell className="text-center text-sm">{m.regimeTributacao}</TableCell>
                <TableCell><Badge variant={tribVariant(m.tributacao)}>{m.tributacao ?? "—"}</Badge></TableCell>
                <TableCell className="text-center font-mono text-sm">{m.cstPis ?? "—"}</TableCell>
                <TableCell className="text-center text-sm">{m.aliqPis.toFixed(2)}</TableCell>
                <TableCell className="text-center text-sm">{m.aliqCofins.toFixed(2)}</TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{m.grupoBeneficiado ?? "—"}</TableCell>
                <TableCell>
                  {podeEditar && (
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(m.id)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(m.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      {query.data && (
        <PaginacaoRodape
          pagina={pagina}
          totalPaginas={query.data.totalPaginas}
          totalItens={totalItens}
          primeiroItem={primeiroItem}
          ultimoItem={ultimoItem}
          tamanhoPagina={tamanhoPagina}
          onPaginaChange={setPagina}
          onTamanhoPaginaChange={setTamanhoPagina}
        />
      )}

      {/* Dialog Criar / Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? "Editar Monofásico" : "Novo Monofásico"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1">
                <Label>NCM/SH <span className="text-destructive">*</span></Label>
                <NcmSearchCombobox
                  valueCodigo={form.codigoNcm ?? ""}
                  onChange={(_ci, codigo) => set("codigoNcm", codigo)}
                  placeholder="Buscar NCM..."
                />
              </div>

              <div className="space-y-1">
                <Label>Segmento de Atividade</Label>
                <Input type="number" min={0} value={form.segmentoAtividade}
                  onChange={e => set("segmentoAtividade", parseInt(e.target.value) || 0)} />
              </div>
              <div className="space-y-1">
                <Label>Regime de Tributação</Label>
                <Input type="number" min={0} value={form.regimeTributacao}
                  onChange={e => set("regimeTributacao", parseInt(e.target.value) || 0)} />
              </div>

              <div className="space-y-1">
                <Label>Tributação</Label>
                <Select value={form.tributacao || "Monofásico"} onValueChange={v => set("tributacao", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TRIBUTACOES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Vigência</Label>
                <Input value={form.vigencia ?? ""} onChange={e => set("vigencia", e.target.value)} maxLength={20} />
              </div>

              <div className="space-y-1">
                <Label>CST PIS</Label>
                <CstSelect value={form.cstPis ?? ""} options={pisOpts} onChange={v => set("cstPis", v)} />
              </div>
              <div className="space-y-1">
                <Label>CST COFINS</Label>
                <CstSelect value={form.cstCofins ?? ""} options={pisOpts} onChange={v => set("cstCofins", v)} />
              </div>

              <div className="space-y-1">
                <Label>% PIS</Label>
                <Input type="number" step="0.0001" min="0" value={form.aliqPis}
                  onChange={e => set("aliqPis", parseFloat(e.target.value) || 0)} />
              </div>
              <div className="space-y-1">
                <Label>% COFINS</Label>
                <Input type="number" step="0.0001" min="0" value={form.aliqCofins}
                  onChange={e => set("aliqCofins", parseFloat(e.target.value) || 0)} />
              </div>

              <div className="flex items-center gap-2">
                <Checkbox id="aliqCond" checked={form.aliquotaCondicionada}
                  onCheckedChange={v => set("aliquotaCondicionada", v === true)} />
                <Label htmlFor="aliqCond">Alíquota Condicionada</Label>
              </div>
              <div className="space-y-1">
                <Label>Novo NCM (substituição)</Label>
                <Input value={form.novoNcm ?? ""} onChange={e => set("novoNcm", e.target.value)} maxLength={10} />
              </div>

              <div className="space-y-1">
                <Label>Início Vigência</Label>
                <Input type="date" value={form.dtInicioVigencia ?? ""} onChange={e => set("dtInicioVigencia", e.target.value || null)} />
              </div>
              <div className="space-y-1">
                <Label>Fim Vigência</Label>
                <Input type="date" value={form.dtFimVigencia ?? ""} onChange={e => set("dtFimVigencia", e.target.value || null)} />
              </div>

              <div className="col-span-2 space-y-1">
                <Label>Grupo Beneficiado</Label>
                <Input value={form.grupoBeneficiado ?? ""} onChange={e => set("grupoBeneficiado", e.target.value)} maxLength={120} />
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-1">
                <Label>Condições</Label>
                <Textarea value={form.condicoes ?? ""} onChange={e => set("condicoes", e.target.value)} maxLength={700} rows={2} />
              </div>
              <div className="space-y-1">
                <Label>Origem da Informação</Label>
                <Input value={form.origemInformacao ?? ""} onChange={e => set("origemInformacao", e.target.value)} maxLength={255} />
              </div>
              <div className="space-y-1">
                <Label>Base Legal</Label>
                <Textarea value={form.baseLegal ?? ""} onChange={e => set("baseLegal", e.target.value)} maxLength={700} rows={2} />
              </div>
              <div className="space-y-1">
                <Label>Observações</Label>
                <Textarea value={form.observacoes ?? ""} onChange={e => set("observacoes", e.target.value)} maxLength={700} rows={2} />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Excluir */}
      <AlertDialog open={deleteId !== null} onOpenChange={open => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Monofásico?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
